import { clsx } from 'clsx';
import { Search } from 'lucide-react';
import { forwardRef, useEffect, useId, useRef, useState, type InputHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { aplicarMascara, formatarData, formatarNumero, lerData, lerNumero, mascararData } from '../utils/formatar';

/** Props comuns a todos os campos (07 §9). */
export interface CampoBaseProps {
  nome: string;
  rotulo: string;
  erro?: string;
  ajuda?: string;
  obrigatorio?: boolean;
  somenteLeitura?: boolean;
  desabilitado?: boolean;
  validando?: boolean;
  destaque?: boolean;
  className?: string;
  /** Colunas de 12 na grade do formulário. */
  largura?: number;
}

interface MolduraProps extends CampoBaseProps {
  id: string;
  children: ReactNode;
  afixoAntes?: ReactNode;
  afixoDepois?: ReactNode;
}

function Moldura({ id, nome, rotulo, erro, ajuda, obrigatorio, somenteLeitura, validando, destaque, className, largura, children, afixoAntes, afixoDepois }: MolduraProps) {
  return (
    <div
      className={clsx('campo', destaque && 'campo-destaque', className)}
      data-campo={nome}
      data-invalido={erro ? 'true' : undefined}
      data-somente-leitura={somenteLeitura ? 'true' : undefined}
      style={largura ? { gridColumn: `span ${largura}` } : undefined}
    >
      <label htmlFor={id} className="campo-rotulo">
        {rotulo}
        {obrigatorio && <span className="obrigatorio" aria-hidden="true">*</span>}
      </label>
      <div className="campo-controle">
        {afixoAntes && <span className="afixo">{afixoAntes}</span>}
        {children}
        {afixoDepois && <span className="afixo">{afixoDepois}</span>}
        {validando && <span className="validando" aria-hidden="true" />}
      </div>
      {erro ? <span id={`${id}-erro`} className="campo-mensagem">{erro}</span> : ajuda ? <span id={`${id}-ajuda`} className="campo-ajuda">{ajuda}</span> : null}
    </div>
  );
}

function ariaDe(id: string, p: CampoBaseProps) {
  return {
    id,
    name: p.nome,
    'aria-invalid': p.erro ? true : undefined,
    'aria-required': p.obrigatorio || undefined,
    'aria-describedby': p.erro ? `${id}-erro` : p.ajuda ? `${id}-ajuda` : undefined,
    readOnly: p.somenteLeitura,
    disabled: p.desabilitado,
  } as const;
}

type NativoTexto = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onBlur' | 'size' | 'prefix'>;

export interface CampoTextoProps extends CampoBaseProps, NativoTexto {
  valor: string;
  aoMudar: (valor: string) => void;
  aoSair?: (valor: string) => void;
  tamanho?: number;
  mascara?: string;
  mono?: boolean;
  prefixo?: ReactNode;
  sufixo?: ReactNode;
  /** 'maiusculo' aplica na digitação, como a gravação faria. */
  transformar?: 'maiusculo';
}

export const CampoTexto = forwardRef<HTMLInputElement, CampoTextoProps>(function CampoTexto(props, ref) {
  const { valor, aoMudar, aoSair, tamanho, mascara, mono, prefixo, sufixo, transformar, nome, rotulo, erro, ajuda, obrigatorio, somenteLeitura,
    desabilitado, validando, destaque, className, largura, ...nativo } = props;
  const id = useId();
  const base = { nome, rotulo, erro, ajuda, obrigatorio, somenteLeitura, desabilitado, validando, destaque, className, largura };
  return (
    <Moldura id={id} {...base} afixoAntes={prefixo} afixoDepois={sufixo}>
      <input
        ref={ref}
        {...ariaDe(id, base)}
        {...nativo}
        className={mono ? 'mono' : undefined}
        value={valor}
        maxLength={mascara ? mascara.length : tamanho}
        // Desligado por padrão (cadastros); quem pede (login: username, current-password) é respeitado.
        autoComplete={nativo.autoComplete ?? 'off'}
        spellCheck={false}
        onChange={(e) => {
          let v = mascara ? aplicarMascara(e.target.value, mascara) : e.target.value;
          if (transformar === 'maiusculo') v = v.toUpperCase();
          aoMudar(v);
        }}
        onBlur={(e) => aoSair?.(e.target.value)}
      />
    </Moldura>
  );
});

export interface CampoNumeroProps extends CampoBaseProps, Omit<NativoTexto, 'min' | 'max'> {
  valor: number | null;
  aoMudar: (valor: number | null) => void;
  aoSair?: (valor: number | null) => void;
  decimais: number;
  min?: number;
  max?: number;
  prefixo?: ReactNode;
  sufixo?: ReactNode;
}

/** Digita livre (1.234,5), formata ao sair; setas não alteram valor (07 §3.3). */
export const CampoNumero = forwardRef<HTMLInputElement, CampoNumeroProps>(function CampoNumero(props, ref) {
  const { valor, aoMudar, aoSair, decimais, min, max, prefixo, sufixo, nome, rotulo, erro, ajuda, obrigatorio, somenteLeitura, desabilitado, validando,
    destaque, className, largura, ...nativo } = props;
  const id = useId();
  const [texto, setTexto] = useState(() => formatarNumero(valor, decimais));
  const [editando, setEditando] = useState(false);
  useEffect(() => {
    if (!editando) setTexto(formatarNumero(valor, decimais));
  }, [valor, decimais, editando]);
  const base = { nome, rotulo, erro, ajuda, obrigatorio, somenteLeitura, desabilitado, validando, destaque, className, largura };
  const foraDaFaixa = valor !== null && ((min !== undefined && valor < min) || (max !== undefined && valor > max));
  return (
    <Moldura id={id} {...base} erro={erro ?? (foraDaFaixa ? `Valor fora da faixa${min !== undefined ? ` (mín. ${formatarNumero(min, decimais)})` : ''}` : undefined)}
      afixoAntes={prefixo} afixoDepois={sufixo}>
      <input
        ref={ref}
        {...ariaDe(id, base)}
        {...nativo}
        inputMode={decimais > 0 ? 'decimal' : 'numeric'}
        data-alinhar="direita"
        value={texto}
        autoComplete="off"
        onFocus={() => setEditando(true)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
          nativo.onKeyDown?.(e);
        }}
        onChange={(e) => {
          const t = e.target.value.replace(/[^\d.,-]/g, '');
          setTexto(t);
          aoMudar(lerNumero(t));
        }}
        onBlur={() => {
          setEditando(false);
          const n = lerNumero(texto);
          const arredondado = n === null ? null : Number(n.toFixed(decimais));
          setTexto(formatarNumero(arredondado, decimais));
          if (arredondado !== valor) aoMudar(arredondado);
          aoSair?.(arredondado);
        }}
      />
    </Moldura>
  );
});

export interface CampoDataProps extends CampoBaseProps, NativoTexto {
  /** 'AAAA-MM-DD' ou null. */
  valor: string | null;
  aoMudar: (valor: string | null) => void;
  aoSair?: (valor: string | null) => void;
  dataBase: string;
  hoje?: string;
}

/** 290926 → 29/09/2026; h = hoje; b = data-base; +7 = hoje + 7 (07 §3.3). */
export const CampoData = forwardRef<HTMLInputElement, CampoDataProps>(function CampoData(props, ref) {
  const { valor, aoMudar, aoSair, dataBase, hoje = new Date().toISOString().slice(0, 10), nome, rotulo, erro, ajuda, obrigatorio, somenteLeitura,
    desabilitado, validando, destaque, className, largura, ...nativo } = props;
  const id = useId();
  const [texto, setTexto] = useState(() => formatarData(valor));
  const [invalida, setInvalida] = useState(false);
  useEffect(() => setTexto(formatarData(valor)), [valor]);
  const base = { nome, rotulo, erro: erro ?? (invalida ? 'Data inválida — use DD/MM/AAAA, h (hoje) ou b (data-base)' : undefined), ajuda, obrigatorio,
    somenteLeitura, desabilitado, validando, destaque, className, largura };
  return (
    <Moldura id={id} {...base}>
      <input
        ref={ref}
        {...ariaDe(id, base)}
        {...nativo}
        inputMode="numeric"
        placeholder="dd/mm/aaaa"
        value={texto}
        autoComplete="off"
        onChange={(e) => setTexto(mascararData(e.target.value))}
        onBlur={() => {
          const iso = texto.trim() === '' ? null : lerData(texto, hoje, dataBase);
          const ruim = texto.trim() !== '' && iso === null;
          setInvalida(ruim);
          if (!ruim) {
            setTexto(formatarData(iso));
            if (iso !== valor) aoMudar(iso);
            aoSair?.(iso);
          }
        }}
      />
    </Moldura>
  );
});

export interface SelecaoProps extends CampoBaseProps {
  valor: string | null;
  aoMudar: (valor: string | null) => void;
  aoSair?: (valor: string | null) => void;
  opcoes: { valor: string; rotulo: string }[];
}

export const Selecao = forwardRef<HTMLSelectElement, SelecaoProps>(function Selecao(props, ref) {
  const { valor, aoMudar, aoSair, opcoes, ...base } = props;
  const id = useId();
  return (
    <Moldura id={id} {...base}>
      <select
        ref={ref}
        {...ariaDe(id, base)}
        disabled={base.desabilitado || base.somenteLeitura}
        value={valor ?? ''}
        onChange={(e) => aoMudar(e.target.value || null)}
        onBlur={(e) => aoSair?.(e.target.value || null)}
      >
        <option value="">{base.obrigatorio ? 'Selecione…' : '—'}</option>
        {opcoes.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
      </select>
    </Moldura>
  );
});

export interface InterruptorProps extends CampoBaseProps {
  valor: boolean;
  aoMudar: (valor: boolean) => void;
  aoSair?: (valor: boolean) => void;
}

/** Espaço alterna (07 §3.3). */
export const Interruptor = forwardRef<HTMLButtonElement, InterruptorProps>(function Interruptor(props, ref) {
  const { valor, aoMudar, aoSair, nome, rotulo, erro, ajuda, somenteLeitura, desabilitado, className, largura } = props;
  const id = useId();
  return (
    <div className={clsx('campo', className)} data-campo={nome} style={largura ? { gridColumn: `span ${largura}` } : undefined}>
      <span className="campo-rotulo" id={`${id}-rotulo`}>{rotulo}</span>
      <div style={{ display: 'flex', alignItems: 'center', height: 'var(--altura-campo)' }}>
        <button
          ref={ref}
          id={id}
          type="button"
          role="switch"
          className="interruptor"
          aria-checked={valor}
          aria-labelledby={`${id}-rotulo`}
          aria-describedby={erro ? `${id}-erro` : undefined}
          disabled={desabilitado || somenteLeitura}
          onClick={() => aoMudar(!valor)}
          onBlur={() => aoSair?.(valor)}
        />
      </div>
      {erro ? <span id={`${id}-erro`} className="campo-mensagem">{erro}</span> : ajuda ? <span className="campo-ajuda">{ajuda}</span> : null}
    </div>
  );
});

export interface AreaTextoProps extends CampoBaseProps {
  valor: string;
  aoMudar: (valor: string) => void;
  aoSair?: (valor: string) => void;
  tamanho?: number;
}

export const AreaTexto = forwardRef<HTMLTextAreaElement, AreaTextoProps>(function AreaTexto(props, ref) {
  const { valor, aoMudar, aoSair, tamanho, ...base } = props;
  const id = useId();
  return (
    <Moldura id={id} {...base}>
      <textarea ref={ref} {...ariaDe(id, base)} rows={3} maxLength={tamanho} value={valor} onChange={(e) => aoMudar(e.target.value)}
        onBlur={(e) => aoSair?.(e.target.value)} />
    </Moldura>
  );
});

export interface OpcaoConsulta { id: number | string; rotulo: string }

export interface CampoConsultaProps extends CampoBaseProps {
  valor: string;
  aoMudar: (valor: string) => void;
  aoSair?: (valor: string) => void;
  /** Descrição do registro escolhido. Com `aoBuscar`, é o que o campo mostra; sem, fica ao lado do código. */
  descricao?: string;
  /** F3 ou lupa (07 §3.3). */
  aoConsultar: (textoAtual: string) => void;
  /** Busca enquanto digita: devolve as opções da lista. Sem ela, o campo aceita só o código (comportamento antigo). */
  aoBuscar?: (texto: string, sinal: AbortSignal) => Promise<OpcaoConsulta[]>;
  aoEscolher?: (opcao: OpcaoConsulta) => void;
}

const ATRASO_BUSCA_MS = 180;

export const CampoConsulta = forwardRef<HTMLInputElement, CampoConsultaProps>(function CampoConsulta(props, ref) {
  const { valor, aoMudar, aoSair, descricao, aoConsultar, aoBuscar, aoEscolher, ...base } = props;
  const id = useId();
  const escolhido = descricao ?? valor;
  const [texto, setTexto] = useState(escolhido);
  const [opcoes, setOpcoes] = useState<OpcaoConsulta[]>([]);
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(0);
  const [buscando, setBuscando] = useState(false);
  const digitando = useRef(false);
  const controle = useRef<AbortController | null>(null);
  const espera = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!digitando.current) setTexto(escolhido);
  }, [escolhido]);
  useEffect(() => () => { controle.current?.abort(); if (espera.current) clearTimeout(espera.current); }, []);

  if (!aoBuscar) {
    return (
      <Moldura id={id} {...base} afixoDepois={descricao ? <span className="texto-2">{descricao}</span> : undefined}>
        <input
          ref={ref}
          {...ariaDe(id, base)}
          className="mono"
          value={valor}
          autoComplete="off"
          aria-keyshortcuts="F3"
          onChange={(e) => aoMudar(e.target.value)}
          onBlur={(e) => aoSair?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'F3') {
              e.preventDefault();
              aoConsultar(valor);
            }
          }}
        />
        {!base.somenteLeitura && (
          <button type="button" className="botao botao-fantasma botao-icone" tabIndex={-1} aria-label={`Consultar ${base.rotulo} (F3)`} onClick={() => aoConsultar(valor)}>
            <Search size={16} strokeWidth={1.75} />
          </button>
        )}
      </Moldura>
    );
  }

  const buscar = (t: string) => {
    if (espera.current) clearTimeout(espera.current);
    controle.current?.abort();
    if (!t.trim()) { setOpcoes([]); setAberto(false); setBuscando(false); return; }
    setBuscando(true);
    espera.current = setTimeout(() => {
      const ac = new AbortController();
      controle.current = ac;
      aoBuscar(t.trim(), ac.signal).then((r) => {
        if (ac.signal.aborted) return;
        setOpcoes(r); setAtivo(0); setAberto(true); setBuscando(false);
      }).catch(() => { if (!ac.signal.aborted) { setOpcoes([]); setBuscando(false); } });
    }, ATRASO_BUSCA_MS);
  };
  const escolher = (o: OpcaoConsulta) => {
    digitando.current = false;
    setTexto(o.rotulo); setAberto(false); setOpcoes([]);
    aoMudar(String(o.id));
    aoEscolher?.(o);
  };
  /** Sair sem escolher: texto que bate com uma única opção (ou exatamente com uma) vira a escolha; o resto volta ao que estava gravado. */
  const resolver = () => {
    const t = texto.trim().toLowerCase();
    if (!digitando.current) return;
    digitando.current = false;
    if (!t) { aoMudar(''); setTexto(''); return; }
    const exata = opcoes.find((o) => o.rotulo.toLowerCase() === t || o.rotulo.toLowerCase().split(' · ').includes(t));
    const unica = opcoes.length === 1 ? opcoes[0] : undefined;
    const alvo = exata ?? unica;
    if (alvo) escolher(alvo); else setTexto(escolhido);
  };
  const listaId = `${id}-opcoes`;
  return (
    <Moldura id={id} {...base} validando={base.validando || buscando}>
      <input
        ref={ref}
        {...ariaDe(id, base)}
        role="combobox"
        aria-expanded={aberto}
        aria-controls={listaId}
        aria-autocomplete="list"
        aria-activedescendant={aberto && opcoes[ativo] ? `${listaId}-${ativo}` : undefined}
        value={texto}
        autoComplete="off"
        placeholder={base.somenteLeitura ? undefined : 'Digite para buscar…'}
        aria-keyshortcuts="F3"
        onChange={(e) => { digitando.current = true; setTexto(e.target.value); buscar(e.target.value); }}
        onBlur={() => { resolver(); setAberto(false); aoSair?.(valor); }}
        onKeyDown={(e) => {
          if (e.key === 'F3') { e.preventDefault(); setAberto(false); aoConsultar(digitando.current ? texto : ''); }
          else if (e.key === 'ArrowDown' && opcoes.length) { e.preventDefault(); setAberto(true); setAtivo((i) => (i + 1) % opcoes.length); }
          else if (e.key === 'ArrowUp' && opcoes.length) { e.preventDefault(); setAtivo((i) => (i - 1 + opcoes.length) % opcoes.length); }
          else if (e.key === 'Enter' && aberto && opcoes[ativo]) { e.preventDefault(); escolher(opcoes[ativo]); }
          else if (e.key === 'Escape' && aberto) { e.preventDefault(); e.stopPropagation(); digitando.current = false; setAberto(false); setTexto(escolhido); }
        }}
      />
      {!base.somenteLeitura && (
        <button type="button" className="botao botao-fantasma botao-icone" tabIndex={-1} aria-label={`Consultar ${base.rotulo} (F3)`}
          onClick={() => aoConsultar(digitando.current ? texto : '')}>
          <Search size={16} strokeWidth={1.75} />
        </button>
      )}
      {aberto && (
        <ul id={listaId} role="listbox" className="consulta-opcoes" aria-label={`Opções de ${base.rotulo}`}>
          {opcoes.length === 0 && <li className="consulta-vazio" aria-disabled="true">Nada encontrado — F3 abre a busca completa</li>}
          {opcoes.map((o, i) => (
            // mousedown antes do blur: o clique escolhe sem o campo perder o foco primeiro.
            <li key={o.id} id={`${listaId}-${i}`} role="option" aria-selected={i === ativo} onMouseDown={(e) => { e.preventDefault(); escolher(o); }}
              onMouseEnter={() => setAtivo(i)}>{o.rotulo}</li>
          ))}
        </ul>
      )}
    </Moldura>
  );
});
